/**
 * 7-day rain forecast UI (SPEC v0.9.3 Part A). Reads the multi-model ensemble from
 * /api/rain (never Open-Meteo directly). Shows a day-chips bar under the search box
 * (only when a heavy+ day is forecast) and a card at the user's point with per-day
 * bars (median + min–max range), confidence styling, TMD level, and a time phrase.
 * No data → nothing shown. Every value comes from the API (no mock).
 *
 * "วันนี้/พรุ่งนี้" is anchored to the Thai calendar date (thaiToday/thaiTomorrow),
 * not an array index, so the label is right in any browser timezone. Hourly bars are
 * coloured by each hour's rate (hourlyLevelOf) — red only for a genuinely intense
 * hour — with the peak hour marked, not reddened. Both helpers are unit-tested in
 * @flowsight/sim (rain-util).
 */
import { nearWord, placeWord, spotWord } from "./place-word";
import { thaiToday, thaiTomorrow, thaiHour, peakWindow, nowVsPeak, rainLevelOf, hourlyLevelOf, type RainLevel, type AdviceDecision } from "@flowsight/sim";
import { cachedFetch } from "./net-cache";

const LEVEL_TH: Record<RainLevel, string> = { none: "ไม่มีฝน", light: "ฝนเล็กน้อย", moderate: "ฝนปานกลาง", heavy: "ฝนหนัก", veryheavy: "ฝนหนักมาก" };

export interface RainDay { date: string; dow: string; perModel: Record<string, number | null>; m: number; min: number; max: number; median: number; level: string; heavyCount: number; veryHeavyCount: number; isHeavyDay: boolean; chipLabel: string; confidence: string; timePhrase: string }
export interface RainFallback { kind: "province" | "precomputed"; name: string | null; distKm: number | null; fetchedAt: string }
export interface RainData { fallback?: RainFallback; cachedAt?: string; lat: number; lon: number; updatedAt: string; modelsUsed: string[]; modelsExcluded: string[]; anyHeavy: boolean; days: RainDay[]; hourlyMedian: number[][]; disclaimer: string }

export const LEVEL: Record<string, { c: string; th: string }> = {
  none: { c: "#4a5a6e", th: "ไม่มีฝน" }, light: { c: "#4aa3df", th: "ฝนเล็กน้อย" }, moderate: { c: "#2ec7b8", th: "ฝนปานกลาง" },
  heavy: { c: "#ffb020", th: "ฝนหนัก" }, veryheavy: { c: "#ff3b5c", th: "ฝนหนักมาก" },
};
// hourly sparkline palette — red (#ff3b5c) reserved for the "veryheavy" rate only (fix 6)
const SPARK: Record<RainLevel, string> = { none: "#2a3340", light: "#4aa3df", moderate: "#2ec7b8", heavy: "#ffb020", veryheavy: "#ff3b5c" };
const esc = (s: string): string => s.replace(/[<>&"]/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[c] ?? c));
const dayNum = (iso: string): number => Number(iso.slice(8, 10));

/** Day label anchored to the Thai calendar date (fix 3), not the array index. */
export function chipName(d: { date: string; dow: string }): string {
  const now = new Date();
  if (d.date === thaiToday(now)) return "วันนี้";
  if (d.date === thaiTomorrow(now)) return "พรุ่งนี้";
  return `${d.dow} ${dayNum(d.date)}`;
}

export async function loadRain(lat: number, lon: number): Promise<RainData | null> {
  try {
    const r = await cachedFetch(`/api/rain?lat=${lat.toFixed(4)}&lon=${lon.toFixed(4)}`);
    if (!r.ok) return null;
    const d = (await r.json()) as RainData;
    return Array.isArray(d.days) && d.days.length ? d : null;
  } catch { return null; }
}

/* ── day chips bar (fixed, under the search box) ─────────────────────── */
let chipsEl: HTMLElement | null = null;
let onDay: ((i: number) => void) | null = null;

export function setRainDayHandler(fn: (i: number) => void): void { onDay = fn; }

export function showRainChips(data: RainData): void {
  hideRainChips();
  if (!data.anyHeavy) return; // chips only when there's a heavy+ day (SPEC A6 / fix 2)
  const el = document.createElement("div");
  el.className = "rf-chips";
  el.innerHTML = data.days.map((d, i) => {
    const col = LEVEL[d.level]?.c ?? LEVEL.none!.c;
    return `<button class="rf-chip${d.isHeavyDay ? " heavy" : ""}" data-i="${i}" style="${d.isHeavyDay ? `border-color:${col}` : ""}">
      <span class="rf-chip-day">${chipName(d)}</span>${d.chipLabel ? `<span class="rf-chip-lvl" style="color:${col}">${d.chipLabel}</span>` : ""}</button>`;
  }).join("");
  el.querySelectorAll<HTMLElement>(".rf-chip").forEach((b) => b.addEventListener("click", () => {
    el.querySelectorAll(".rf-chip").forEach((x) => x.classList.remove("sel"));
    b.classList.add("sel");
    onDay?.(Number(b.dataset.i));
  }));
  document.body.appendChild(el);
  chipsEl = el;
}
export function hideRainChips(): void { chipsEl?.remove(); chipsEl = null; }

/* ── Part D: "เตรียมตัว" advice (D2/D6) — amber, on top of the rain card ───── */
/** The one-line reason under the Part D advice — built only from the conditions that fired. */
export function adviceReason(d: AdviceDecision, data: RainData, damName: string | null): string {
  const labels = d.days.map((i) => data.days[i]?.dow).filter(Boolean) as string[];
  const dayRange = labels.length === 0 ? "" : labels.length === 1 ? labels[0]! : `${labels[0]}–${labels[labels.length - 1]}`;
  const condB = d.condB === "below" && damName ? `${placeWord()}อยู่ท้ายเขื่อน${damName.replace(/^เขื่อน/, "")}`
    : d.condB === "flood" ? "อยู่ในพื้นที่น้ำท่วม (GISTDA)"
    : d.condB === "thaiwater" ? `มีสถานีน้ำล้นตลิ่ง${nearWord()} (ThaiWater)` : "";
  return `${d.nModels} จาก ${d.mModels} แบบจำลองคาดฝนหนักมาก${dayRange ? " " + dayRange : ""}${condB ? ` · ${condB}` : ""}`;
}

export function adviceEl(d: AdviceDecision, data: RainData, damName: string | null): HTMLElement {
  const srcDays = d.via === "dam" ? data.days /* dam days share dow labels with the point */ : data.days;
  const labels = d.days.map((i) => srcDays[i]?.dow).filter(Boolean) as string[];
  const dayRange = labels.length === 0 ? "" : labels.length === 1 ? labels[0]! : `${labels[0]}–${labels[labels.length - 1]}`;
  const condB = d.condB === "below" && damName ? `${placeWord()}อยู่ท้ายเขื่อน${damName.replace(/^เขื่อน/, "")}`
    : d.condB === "flood" ? "อยู่ในพื้นที่น้ำท่วม (GISTDA)"
    : d.condB === "thaiwater" ? `มีสถานีน้ำล้นตลิ่ง${nearWord()} (ThaiWater)` : "";
  const reason = `${d.nModels} จาก ${d.mModels} แบบจำลองคาดฝนหนักมาก${dayRange ? " " + dayRange : ""}${condB ? ` · ${condB}` : ""}`;
  const ddpm = d.ddpm ? " · พื้นที่นี้มีประกาศเขตภัยพิบัติ (ปภ.)" : ""; // D4: reference the announcement, never decide to leave on our own
  const el = document.createElement("div");
  el.className = "advice-card";
  el.innerHTML = `<div class="adv-main">⚠️ ควรเตรียมยกของขึ้นที่สูง และติดตามประกาศ ปภ./กรมอุตุฯ</div>
    <div class="adv-reason">${esc(reason)}${esc(ddpm)}</div>
    <div class="muted small">ที่มา: พยากรณ์ Open-Meteo · อัปเดต ${new Date(data.updatedAt).toLocaleString("th-TH", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}</div>`;
  return el;
}

/* ── the forecast card (appended into the result host) ──────────────── */
export function rainCard(data: RainData, advice?: HTMLElement | null): HTMLElement {
  const card = document.createElement("div");
  card.className = "rf-card";
  const maxMed = Math.max(1, ...data.days.map((d) => d.max));
  card.innerHTML = `<div class="rf-head">🌧 ฝน 7 วันข้างหน้า ที่${spotWord()}</div>`
    + (data.anyHeavy ? "" : `<div class="muted small">7 วันนี้ไม่มีฝนตกหนักตามพยากรณ์ (ค่ากลางของแบบจำลอง)</div>`);
  const row = document.createElement("div"); row.className = "rf-row";
  data.days.forEach((d, i) => {
    const col = LEVEL[d.level]?.c ?? LEVEL.none!.c;
    const h = Math.round((d.median / maxMed) * 46) + 2;
    const rngTop = Math.round((d.max / maxMed) * 46) + 2, rngBot = Math.round((d.min / maxMed) * 46) + 2;
    const col1 = document.createElement("button");
    col1.className = `rf-day conf-${d.confidence}`;
    col1.innerHTML = `<div class="rf-barwrap">
        <div class="rf-range" style="bottom:${rngBot}px;height:${Math.max(1, rngTop - rngBot)}px"></div>
        <div class="rf-bar" style="height:${h}px;background:${col}"></div></div>
      <div class="rf-med">${d.median}</div>
      <div class="rf-dow">${chipName(d)}</div>`;
    col1.addEventListener("click", () => showHourly(card, data, i));
    row.appendChild(col1);
  });
  card.appendChild(row);
  const detail = document.createElement("div"); detail.className = "rf-detail"; detail.id = "rf-detail";
  card.appendChild(detail);
  const firstHeavy = data.days.findIndex((d) => d.isHeavyDay);
  showHourly(card, data, firstHeavy >= 0 ? firstHeavy : 0);

  // source + time only (no disclaimer wording, v0.9.3) — kept per the no-mock rule
  const up = new Date(data.updatedAt);
  const foot = document.createElement("div"); foot.className = "muted small"; foot.style.marginTop = "8px";
  // v0.9.5a A4: the live call failed and nothing was cached → province-level daily values (no hourly bars)
  const fbNote = data.fallback?.kind === "province"
    ? `<b>ค่าประมาณระดับจังหวัด${data.fallback.name ? esc(data.fallback.name) : ""}</b> (ดึงตรงจุดนี้ไม่ได้ตอนนี้ · ไม่มีรายชั่วโมง) · ` : "";
  foot.innerHTML = `${fbNote}ที่มา: Open-Meteo (${data.modelsUsed.map(esc).join(" · ")})${data.modelsExcluded.length ? ` · (ไม่มีค่าที่จุดนี้: ${data.modelsExcluded.map((m) => esc(m.split(" —")[0]!)).join(", ")})` : ""} · อัปเดต ${up.toLocaleString("th-TH", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}`;
  card.appendChild(foot);
  if (advice) card.insertBefore(advice, card.firstChild); // D6: advice on top of the 7-day rain card
  return card;
}

function confText(c: string): string { return c === "solid" ? "ค่อนข้างแน่" : c === "normal" ? "เป็นไปได้" : "ยังไม่แน่นอน"; }

function showHourly(card: HTMLElement, data: RainData, i: number): void {
  const host = card.querySelector("#rf-detail") as HTMLElement | null; if (!host) return;
  card.querySelectorAll(".rf-day").forEach((e, idx) => e.classList.toggle("sel", idx === i));
  const d = data.days[i]; if (!d) return;
  const col = LEVEL[d.level]?.c ?? LEVEL.none!.c;
  // fix 5: measure LEVEL agreement; say "ทั้ง M เห็นตรงกันว่าไม่มีฝนหนัก" when none is heavy
  const agree = d.heavyCount === 0
    ? `ทั้ง ${d.m} แบบจำลองเห็นตรงกันว่าไม่มีฝนหนัก`
    : `${d.heavyCount} จาก ${d.m} แบบจำลองเห็นว่าฝนหนัก`;
  const range = d.min === d.max ? `${d.median} มม.` : `${d.min}–${d.max} มม. (กลาง ${d.median})`;
  const hrs = data.hourlyMedian[i] ?? [];
  // fix 6: colour each hour by its OWN rate; mark the peak hour (not reddened)
  let peakIdx = -1, peakVal = 0.1;
  hrs.forEach((v, h) => { if (v > peakVal) { peakVal = v; peakIdx = h; } });
  const hmax = Math.max(1, ...hrs);
  // fix 7: for TODAY, dim past hours + draw a "ตอนนี้" line (Thai time)
  const now = new Date();
  const isToday = d.date === thaiToday(now);
  const nowHour = thaiHour(now);
  const spark = hrs.length
    ? `<div class="rf-spark">${hrs.map((v, h) => `<span class="rf-sp${h === peakIdx ? " peak" : ""}${isToday && h < nowHour ? " past" : ""}" style="height:${Math.round((v / hmax) * 22) + 1}px;background:${SPARK[hourlyLevelOf(v)]}" title="${h}:00 ${v} มม./ชม."></span>`).join("")}${isToday ? `<span class="rf-now" style="left:${((nowHour / 24) * 100).toFixed(1)}%" title="ตอนนี้ ${String(nowHour).padStart(2, "0")}:00 น."></span>` : ""}</div><div class="rf-spark-ax"><span>0</span><span>6</span><span>12</span><span>18</span><span>24</span></div>${peakIdx >= 0 ? `<div class="rf-spark-note muted small">● ชั่วโมงหนักสุด ~${String(peakIdx).padStart(2, "0")}:00 น. (${peakVal} มม./ชม.)</div>` : ""}`
    : "";
  // fix 7: now-aware time phrase for today (peak passed / during / before)
  let phrase = d.timePhrase;
  const w = peakWindow(hrs);
  if (isToday && w) {
    const hhmm = (h: number): string => `${String(Math.min(23, h)).padStart(2, "0")}:00`;
    const span = `${hhmm(w.start)}–${hhmm(w.end)} น.`;
    const phase = nowVsPeak(nowHour, w);
    if (phase === "during") phrase = "ตอนนี้อยู่ในช่วงฝนหนักสุดของวัน";
    else if (phase === "after") {
      const remMm = hrs.slice(nowHour).reduce((a, b) => a + b, 0);
      phrase = `ช่วงหนักสุดของวันนี้ผ่านไปแล้ว (${span} ตามพยากรณ์) · ที่เหลือของวัน: ${LEVEL_TH[rainLevelOf(remMm)]} (~${Math.round(remMm * 10) / 10} มม.)`;
    } // "before" → keep the original forecast phrase ("หนักสุด HH:00–HH:00 น.")
  }
  host.innerHTML = `<div class="rf-detail-head"><b style="color:${col}">${chipName(d)} · ${LEVEL[d.level]?.th ?? ""}</b> <span class="rf-conf ${d.confidence}">${confText(d.confidence)}</span></div>
    <div class="muted small">${range} · ${esc(agree)}${phrase ? " · " + esc(phrase) : ""}</div>
    ${spark}`;
}
